import { Link } from 'react-router-dom';
import LegalPage from './LegalPage';

// Source: "InfoRight SaaS Subscription Agreement.docx" in the company OneDrive
// (Legal-TaxEntity-Address/SaaS Subscription Agreement). Keep this page in step
// with that document; mark any new placeholders with <Fill> from LegalPage.
// The agreement's version clause says the published version here is the one in
// force, so bump the version and effective date whenever the text changes.
function SubscriptionAgreement() {
  return (
    <LegalPage
      title="InfoRight SaaS Subscription Agreement"
      lead="The terms that govern your organization's subscription to the InfoRight applications."
      effectiveDate="September 30, 2026"
    >
      <section>
        <h2>Agreement</h2>
        <p>
          This SaaS Subscription Agreement (the "Agreement") is between InfoRight, Inc., 8977 South
          1300 West #2001, West Jordan, UT 84088 ("InfoRight", "we", "us") and the business
          identified in the Order ("Customer", "you").
        </p>
        <p>
          You accept this Agreement by ticking the acceptance box at checkout, signing an Order that
          references it, or using the Services. The person accepting confirms they have authority to
          bind Customer. This Agreement is for business use only and does not apply to consumers.
        </p>
        <p>
          <strong>
            Version 1.0 — effective September 30, 2026. The version in force for
            Customer is the one published at https://www.inforight.com/legal/subscription-agreement
            on the date Customer accepted it. InfoRight keeps prior versions available on request.
          </strong>
        </p>
        <p>
          InfoRight records each acceptance, including the accepting user, the date and time, the IP
          address and the version accepted. That record is evidence of Customer's acceptance of this
          Agreement.
        </p>
      </section>

      <section>
        <h2>1. Definitions</h2>
        <ul>
          <li>
            <strong>Services:</strong> the InfoRight software-as-a-service applications listed in{' '}
            <a href="#schedule-a">Schedule A</a> that Customer subscribes to under an Order, plus
            related support.
          </li>
          <li>
            <strong>Order:</strong> the checkout record, order form or quote accepted by Customer,
            stating the Services, plan, quantities, fees and Subscription Term.
          </li>
          <li>
            <strong>Authorized Users:</strong> Customer's employees and contractors whom Customer
            permits to use the Services.
          </li>
          <li>
            <strong>Customer Data:</strong> all data, records, photos, inspection results, forms and
            files that Customer or its Authorized Users submit to the Services.
          </li>
          <li>
            <strong>Documentation:</strong> InfoRight's user guides and help materials for the
            Services.
          </li>
          <li>
            <strong>Subscription Term:</strong> the period stated in the Order, plus any renewals.
          </li>
        </ul>
      </section>

      <section>
        <h2>2. Subscriptions and Access</h2>
        <p>
          <strong>2.1 Access right.</strong> During the Subscription Term, InfoRight grants Customer
          a non-exclusive, non-transferable right for its Authorized Users to access and use the
          subscribed Services for Customer's internal business operations, in line with the
          Documentation.
        </p>
        <p>
          <strong>2.2 Adding Services.</strong> Customer may add further InfoRight applications at
          any time through a new Order. Each added application is governed by this Agreement and its
          Schedule A description. No separate agreement is needed.
        </p>
        <p>
          <strong>2.3 User accounts.</strong> Customer is responsible for its Authorized Users,
          keeping login credentials secure, and promptly removing access for people who leave. User
          limits and site limits are set in the Order.
        </p>
        <p>
          <strong>2.4 Mobile and offline use.</strong> Where a Service offers mobile or offline
          capture, data syncs when a connection is available. Customer is responsible for devices
          and connectivity at its sites.
        </p>
        <p>
          <strong>2.5 Changes to the Services.</strong> InfoRight may improve and update the
          Services. InfoRight will not materially reduce the core functionality of a subscribed
          Service during a paid Subscription Term.
        </p>
      </section>

      <section>
        <h2>3. Customer Responsibilities and Acceptable Use</h2>
        <p>
          <strong>3.1</strong> Customer is responsible for the accuracy and completeness of Customer
          Data, including inspection entries, photos and sign-offs made by its Authorized Users.
        </p>
        <p>
          <strong>3.2</strong> Customer will not, and will not allow others to:
        </p>
        <ul>
          <li>resell, sublicense or share the Services outside Customer's business;</li>
          <li>copy, reverse engineer or build a competing product from the Services;</li>
          <li>upload malicious code or unlawful content, or interfere with the Services;</li>
          <li>bypass security or usage limits; or</li>
          <li>use the Services in breach of applicable law.</li>
        </ul>
        <p>
          <strong>3.3</strong> InfoRight may suspend access if Customer's use threatens the security
          or integrity of the Services, or if fees are more than 30 days overdue after notice.
          InfoRight will restore access promptly once the issue is resolved.
        </p>
      </section>

      <section>
        <h2>4. Safety and Compliance Responsibility</h2>
        <p>
          <strong>4.1 Tool, not a safety program.</strong> The Services are record-keeping and
          workflow tools that help Customer carry out vehicle, equipment, area and compliance
          inspections. They do not replace Customer's safety management system, qualified
          personnel, professional judgment or physical inspections.
        </p>
        <p>
          <strong>4.2 Customer remains responsible.</strong> Customer alone is responsible for
          workplace safety at its sites and for complying with all laws that apply to its
          operations. This includes mine safety laws and regulations, such as MSHA in the United
          States or applicable provincial or national mining regulators in other countries, and any
          permit or reporting obligations.
        </p>
        <p>
          <strong>4.3 Checklists and templates.</strong> Any inspection checklists, templates or
          content InfoRight provides are examples only. Customer must review and adapt them to its
          equipment, sites and legal requirements before use.
        </p>
        <p>
          <strong>4.4 No guarantee of outcomes.</strong> InfoRight does not warrant that use of the
          Services will prevent accidents, injuries, equipment failure, citations or regulatory
          findings.
        </p>
        <p>
          <strong>4.5 Alerts and notifications.</strong> Notifications, reminders and alerts depend
          on connectivity, device settings and correct configuration. Customer must not rely on them
          as the only means of identifying an unsafe condition.
        </p>
      </section>

      <section>
        <h2>5. Fees, Billing and Renewal</h2>
        <p>
          <strong>5.1 Fees.</strong> Customer will pay the fees stated in the Order. Fees are based
          on the Services and quantities purchased, not actual use, and are non-refundable except as
          this Agreement states.
        </p>
        <p>
          <strong>5.2 Payment.</strong> For card payments, Customer authorizes InfoRight and its
          payment processor to charge the card on file for all fees when due, including renewals.
          For invoiced accounts, invoices are due within 30 days.
        </p>
        <p>
          <strong>5.3 Automatic renewal.</strong> Each Subscription Term renews automatically for
          the same length unless either party gives notice of non-renewal at least 30 days before
          the term ends. InfoRight will send a renewal reminder before charging annual renewals.
        </p>
        <p>
          <strong>5.4 Price changes.</strong> InfoRight may change prices for a renewal term by
          giving at least 60 days' notice before the renewal.
        </p>
        <p>
          <strong>5.5 Taxes.</strong> Fees exclude taxes. Customer pays all applicable sales, use,
          GST/VAT and similar taxes, other than taxes on InfoRight's income.
        </p>
        <p>
          <strong>5.6 Late payment.</strong> Overdue amounts may accrue interest at 1.5% per month
          or the maximum legal rate, if lower.
        </p>
      </section>

      <section>
        <h2>6. Customer Data, Privacy and Security</h2>
        <p>
          <strong>6.1 Ownership.</strong> Customer owns all Customer Data. Customer grants InfoRight
          a limited license to host, process and display Customer Data only to provide, secure and
          support the Services.
        </p>
        <p>
          <strong>6.2 Aggregated data.</strong> InfoRight may use de-identified, aggregated data
          that does not identify Customer, its sites or any individual to operate and improve the
          Services.
        </p>
        <p>
          <strong>6.3 Security.</strong> InfoRight will maintain reasonable administrative,
          technical and physical safeguards, including encryption in transit and at rest, access
          controls and regular backups. InfoRight will notify Customer without undue delay after
          confirming a security breach affecting Customer Data.
        </p>
        <p>
          <strong>6.4 Personal data.</strong> Where Customer Data includes personal information
          (such as inspector names, signatures or photos), InfoRight processes it on Customer's
          behalf under the InfoRight <Link to="/legal/privacy">Privacy Policy</Link> and, where
          required, the{' '}
          <Link to="/legal/data-processing-addendum">Data Processing Addendum</Link>.
        </p>
        <p>
          <strong>6.5 Hosting location.</strong> Customer Data is hosted in the United States.
        </p>
        <p>
          <strong>6.6 Records retention.</strong> Inspection records may be needed for regulatory
          purposes. Customer is responsible for exporting and keeping records for any legally
          required retention period beyond the Subscription Term.
        </p>
      </section>

      <section>
        <h2>7. Confidentiality and Intellectual Property</h2>
        <p>
          <strong>7.1 Confidentiality.</strong> Each party will keep the other's non-public
          business, technical and financial information confidential. It will use that information
          only to perform this Agreement, protecting it with at least reasonable care. This does not
          apply to information that is public, already known, independently developed or lawfully
          received from a third party. A party may disclose information when required by law, after
          giving notice where legally allowed.
        </p>
        <p>
          <strong>7.2 InfoRight IP.</strong> InfoRight and its licensors own the Services,
          Documentation, templates and all related software and intellectual property. No rights are
          granted except those expressly stated.
        </p>
        <p>
          <strong>7.3 Feedback.</strong> InfoRight may freely use suggestions and feedback Customer
          provides about the Services.
        </p>
      </section>

      <section>
        <h2>8. Support, Availability and Warranties</h2>
        <p>
          <strong>8.1 Support.</strong> InfoRight provides email support at{' '}
          <a href="mailto:support@inforight.com">support@inforight.com</a> during U.S. business
          hours in the Mountain Standard Timezone, with response targets as set out in the Order or
          support policy.
        </p>
        <p>
          <strong>8.2 Availability.</strong> InfoRight will use commercially reasonable efforts to
          make the Services available 99.5% of each month, excluding scheduled maintenance and
          events outside its reasonable control.
        </p>
        <p>
          <strong>8.3 Limited warranty.</strong> InfoRight warrants that the Services will perform
          materially as described in the Documentation. If they do not, Customer's remedy is for
          InfoRight to fix the issue. If InfoRight cannot fix it within 30 days, Customer may
          terminate the affected Service and receive a pro-rated refund of prepaid fees.
        </p>
        <p>
          <strong>8.4 Disclaimer.</strong> Except as stated in this Agreement, the Services are
          provided "as is". InfoRight disclaims all other warranties, including merchantability,
          fitness for a particular purpose and uninterrupted or error-free operation, to the extent
          the law allows.
        </p>
      </section>

      <section>
        <h2>9. Limitation of Liability and Indemnities</h2>
        <p>
          <strong>9.1 Excluded damages.</strong> Neither party is liable for indirect, incidental,
          special, consequential or punitive damages. This includes lost profits, lost production,
          downtime, regulatory fines or penalties, even if advised of the possibility.
        </p>
        <p>
          <strong>9.2 Liability cap.</strong> Each party's total liability under this Agreement is
          limited to the greater of (a) the fees Customer paid or owes for the Services in the 12
          months before the event giving rise to the claim, or (b) one month's fees under
          Customer's Order.
        </p>
        <p>
          <strong>9.3 Exceptions.</strong> The limits in 9.1 and 9.2 do not apply to Customer's
          payment obligations, either party's indemnity obligations, breach of confidentiality, or
          liability that cannot be limited by law (such as fraud, or death or personal injury caused
          by a party's negligence).
        </p>
        <p>
          <strong>9.4 InfoRight indemnity.</strong> InfoRight will defend Customer against
          third-party claims that the Services infringe intellectual property rights, and pay
          resulting damages and costs finally awarded.
        </p>
        <p>
          <strong>9.5 Customer indemnity.</strong> Customer will defend InfoRight against
          third-party claims arising from Customer Data, Customer's breach of Section 3, or the
          operation of Customer's sites, equipment and vehicles. This includes claims arising from
          workplace incidents and injuries at Customer's sites.
        </p>
        <p>
          <strong>9.6 Process.</strong> The indemnified party must give prompt notice, allow the
          other party to control the defense, and provide reasonable help.
        </p>
      </section>

      <section>
        <h2>10. Term, Termination and Data Export</h2>
        <p>
          <strong>10.1 Term.</strong> This Agreement lasts while any Subscription Term is in effect.
        </p>
        <p>
          <strong>10.2 Termination for breach.</strong> Either party may terminate this Agreement or
          an affected Order if the other party materially breaches it and does not cure the breach
          within 30 days after written notice.
        </p>
        <p>
          <strong>10.3 Effect of termination.</strong> On termination, Customer's access ends and
          unpaid fees become due. If Customer terminates for InfoRight's uncured breach, InfoRight
          will refund prepaid fees for the unused period.
        </p>
        <p>
          <strong>10.4 Data export.</strong> For 60 days after termination, Customer may export its
          Customer Data in a standard format such as CSV or PDF. After that period, InfoRight will
          delete Customer Data, except backups deleted on their normal cycle and data InfoRight must
          keep by law.
        </p>
        <p>
          <strong>10.5 Survival.</strong> Sections on fees owed, confidentiality, IP, disclaimers,
          liability, indemnities and general terms survive termination.
        </p>
      </section>

      <section>
        <h2>11. General Terms</h2>
        <p>
          <strong>11.1 Order of precedence.</strong> If documents conflict, this order applies: (a)
          a separately signed master agreement between the parties covering the Services, (b) an
          Order, (c) this Agreement, (d) Schedule A, and (e) the Documentation. Terms in a Customer
          purchase order are for administration only and do not apply.
        </p>
        <p>
          <strong>11.2 Changes to this Agreement.</strong> InfoRight may update this Agreement by
          giving at least 30 days' notice. Changes apply from Customer's next renewal,
          unless Customer agrees earlier or the change is required by law.
        </p>
        <p>
          <strong>11.3 Governing law.</strong> This Agreement is governed by the laws of Utah,
          United States.
        </p>
        <p>
          <strong>11.4 Force majeure.</strong> Neither party is liable for delays caused by events
          beyond its reasonable control, other than payment obligations.
        </p>
        <p>
          <strong>11.5 Assignment.</strong> Neither party may assign this Agreement without the
          other's consent, except to a successor in a merger or sale of substantially all related
          assets.
        </p>
        <p>
          <strong>11.6 Publicity.</strong> InfoRight may list Customer's name and logo as a customer
          unless Customer opts out by notice.
        </p>
        <p>
          <strong>11.7 Notices.</strong> Notices must be in writing to the addresses in the Order or
          account, with email counting as written notice.
        </p>
        <p>
          <strong>11.8 Entire agreement.</strong> This Agreement, with Orders and Schedules, is the
          entire agreement on its subject. If any term is unenforceable, the rest remains in effect.
          A failure to enforce a term is not a waiver.
        </p>
        <p>
          <strong>11.9 Related policies.</strong> Use of the Services is also subject to InfoRight's{' '}
          <Link to="/legal/acceptable-use">Acceptable Use Policy</Link> and{' '}
          <Link to="/legal/privacy">Privacy Policy</Link>. If either policy conflicts with this
          Agreement, this Agreement controls.
        </p>
      </section>

      <section id="schedule-a">
        <h2>Schedule A: Service Descriptions</h2>
        <p>
          Each application below is available on its own or together. New InfoRight applications
          are added to this Schedule as they are released.
        </p>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Application</th>
                <th>What it does</th>
                <th>Specific terms</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Light Vehicle Inspection</td>
                <td>
                  Pre-shift and periodic vehicle inspections on mobile devices, with defect
                  reporting, photos, sign-off and history by vehicle.
                </td>
                <td>
                  Customer decides whether a vehicle is fit for use. Flagged defects do not
                  automatically take a vehicle out of service unless user downs the vehicle.
                </td>
              </tr>
              <tr>
                <td>Site Visitor Approval</td>
                <td>
                  Site visitor training acknowledgement, approval routing workflow, pre-arrival
                  clearance records, and audit-ready history.
                </td>
                <td>
                  Customer determines whether visitor has successfully completed training. Visitor
                  approval does not replace customer's need to accompany visitor.
                </td>
              </tr>
              <tr>
                <td>Mine Certification Management</td>
                <td>
                  Tracks and stores MSHA 5000-23 training documentation, reports on demand, keeps
                  information current and retrievable.
                </td>
                <td>Customer determines whether they have certified for each area.</td>
              </tr>
              <tr>
                <td>Area Inspections</td>
                <td>
                  Workplace and area examinations, compliance checklists, corrective actions,
                  due-date tracking and audit-ready reports.
                </td>
                <td>
                  Checklists must be aligned by Customer to applicable regulations. Reports support,
                  but do not certify, regulatory compliance.
                </td>
              </tr>
              <tr>
                <td>Equipment Inspections</td>
                <td>
                  Inspections for fixed and mobile equipment, maintenance findings, recurring
                  schedules and records by asset.
                </td>
                <td>
                  The Service does not replace manufacturer maintenance requirements or qualified
                  mechanical inspection.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3">
          <strong>Plan details:</strong> user and site limits, storage allowances and any
          integrations are set out in the Order.
        </p>
      </section>
    </LegalPage>
  );
}

export default SubscriptionAgreement;
